import { Button, ButtonLevel, ButtonSize, RadialProgress, Text, TextColor, TextSize } from '@vizit/atoms';
import { AlertCircleIcon, WarningTriangleIcon } from '@vizit/icons';
import { colors, cx } from '@vizit/theme';
import { plural } from '@vizit/utils';
import type * as React from 'react';

export interface ScoringStatusProps {
  /**
   * The category being scored; shows "Scoring against {name}…" while set
   *
   * @optional
   */
  scoringCategoryName?: string | null;
  /**
   * How far that scoring got, shown after the status as "12 of 32"
   *
   * @optional
   */
  scoringProgress?: { scored: number; total: number } | null;
  /**
   * The selected category whose scoring failed; shows an error status with the reason when known
   *
   * @optional
   */
  failedScoring?: { categoryName: string; reason?: string | null } | null;
  /**
   * The images the selected category's settled scoring left without a score: the names known (e.g. the ones on
   * screen), and how many there are in all when known, which is shown alone when none is named. Named after a failure,
   * or on their own once it settled
   *
   * @optional
   */
  unscoredImages?: { names: string[]; count?: number | null } | null;
  /**
   * The benchmark picked with no category to score against; shows "Pick a category to score against {name}"
   *
   * @optional
   */
  missingCategoryBenchmarkName?: string | null;
  /**
   * The selected benchmark while it has fewer images than it needs to score, with how many more it needs; shows
   * "{name} needs N more images to score."
   *
   * @optional
   */
  underMinimumBenchmark?: { name: string; imagesNeeded: number } | null;
  /**
   * Where the under-minimum status's "Add images" link leads, the benchmark's page; leave unset to offer no link
   *
   * @optional
   */
  addBenchmarkImagesHref?: string | null;
  /**
   * Called on a plain click of "Add images", to navigate within the app; a modified click (new tab) is left to the
   * browser
   *
   * @optional
   */
  onAddBenchmarkImages?: (() => void) | null;
  /**
   * Set while the benchmark's categories failed to load; shows that they couldn't load with "Try again", which calls it
   *
   * @optional
   */
  onRetryCategories?: (() => void) | null;
}

/** How many unscored images are named before the rest are only counted. */
const NAMED_UNSCORED_IMAGES = 3;

/**
 * "a.png", "a.png and b.png", "a.png, b.png, c.png and 3 more": the first few by name, the rest counted; "2 images"
 * when none is named.
 */
function formatUnscoredImages(names: string[], count?: number | null) {
  if (names.length === 0) {
    return count ? `${count} ${count === 1 ? 'image' : 'images'}` : null;
  }
  const named = names.slice(0, NAMED_UNSCORED_IMAGES);
  const rest = Math.max(count ?? 0, names.length) - named.length;
  if (rest > 0) {
    return `${named.join(', ')} and ${rest} more`;
  }
  return named.length > 1 ? `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}` : (named[0] ?? '');
}

/** A plain click navigates within the app; any modified click (new tab, new window) is left to the browser. */
function handleAddImagesClick(event: React.MouseEvent<HTMLAnchorElement>, onNavigate?: (() => void) | null) {
  const isModifiedClick = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  if (!onNavigate || isModifiedClick || event.defaultPrevented) {
    return;
  }
  event.preventDefault();
  onNavigate();
}

/**
 * The inline status of the selected product category's scoring, wherever a category is picked to score against:
 * that its categories couldn't load with "Try again", "{benchmark} needs N more images to score." while the benchmark
 * is under its minimum (it explains the rest, so only a retry comes first), "Scoring against {category}… N of M" while
 * it runs, "Scoring {category} failed: reason" once it failed, "Unscored: {names}" for the images a settled scoring
 * left without a score, after the failure or on its own, "Pick a category to score against {benchmark}" while the
 * benchmark has none, nothing otherwise. One `output` live region stays mounted, so assistive tech hears each change;
 * while empty it takes no room in the row.
 */
export function ScoringStatus(props: ScoringStatusProps) {
  const { scoringProgress, failedScoring, missingCategoryBenchmarkName, onRetryCategories } = props;
  const { addBenchmarkImagesHref, onAddBenchmarkImages } = props;
  const underMinimum = onRetryCategories ? null : props.underMinimumBenchmark;
  const scoringCategoryName = underMinimum ? null : props.scoringCategoryName;
  const unscoredNames = props.unscoredImages
    ? formatUnscoredImages(props.unscoredImages.names, props.unscoredImages.count)
    : null;
  const isEmpty =
    !underMinimum &&
    !scoringCategoryName &&
    !failedScoring &&
    !missingCategoryBenchmarkName &&
    !onRetryCategories &&
    !unscoredNames;
  // One live region, always mounted: screen readers often skip a region inserted together with its content. While
  // empty it is taken out of the flow, so it adds no gap between its neighbours in a flex row.
  return (
    <output
      className={cx(
        isEmpty ? 'sr-only' : 'flex items-center gap-2',
        !scoringCategoryName && !underMinimum && (failedScoring || onRetryCategories) && 'text-error-600',
      )}
    >
      {underMinimum && (
        <>
          <WarningTriangleIcon size={12} color={colors.warning['500']} aria-hidden />
          <Text size={TextSize.Xs} color={TextColor.Tertiary}>
            {underMinimum.name} needs {plural(underMinimum.imagesNeeded, 'more image')} to score.
          </Text>
          {addBenchmarkImagesHref && (
            <a
              href={addBenchmarkImagesHref}
              onClick={(event) => handleAddImagesClick(event, onAddBenchmarkImages)}
              className="text-sm text-brand-secondary outline-none hover:text-brand-secondary-hover hover:underline focus-visible:ring-2 focus-visible:ring-brand-600"
            >
              Add images
            </a>
          )}
        </>
      )}
      {scoringCategoryName && (
        <>
          <RadialProgress isIndeterminate size={8} aria-hidden />
          <Text size={TextSize.Xs} color={TextColor.Tertiary}>
            Scoring against {scoringCategoryName}…
            {scoringProgress && ` ${scoringProgress.scored} of ${scoringProgress.total}`}
          </Text>
        </>
      )}
      {!scoringCategoryName && !underMinimum && failedScoring && (
        <>
          <AlertCircleIcon size={12} aria-hidden />
          <Text size={TextSize.Xs} className="text-inherit" showEllipsis={false}>
            Scoring {failedScoring.categoryName} failed{failedScoring.reason ? `: ${failedScoring.reason}` : ''}
            {unscoredNames && `. Unscored: ${unscoredNames}`}
          </Text>
        </>
      )}
      {!scoringCategoryName && !underMinimum && !failedScoring && onRetryCategories && (
        <>
          <AlertCircleIcon size={12} aria-hidden />
          <Text size={TextSize.Xs} className="text-inherit">
            Product categories couldn't load.
          </Text>
          <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={() => onRetryCategories()}>
            Try again
          </Button>
        </>
      )}
      {!scoringCategoryName &&
        !underMinimum &&
        !failedScoring &&
        !onRetryCategories &&
        missingCategoryBenchmarkName && (
          <Text size={TextSize.Xs} color={TextColor.Tertiary}>
            Pick a category to score against {missingCategoryBenchmarkName}
          </Text>
        )}
      {!scoringCategoryName &&
        !underMinimum &&
        !failedScoring &&
        !onRetryCategories &&
        !missingCategoryBenchmarkName &&
        unscoredNames && (
          <Text size={TextSize.Xs} color={TextColor.Tertiary} showEllipsis={false}>
            Unscored: {unscoredNames}
          </Text>
        )}
    </output>
  );
}
